// Modules
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { orderBy } from "lodash";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// Type definitions
import { ReimbSummaryDataType } from "redux/types";
import { ReimbDetailsItemMonthlyDeclarations } from "services/api/types";

// Redux
import {
	reimbDetailsSelectorMonthlyDeclarations,
	getLoadingStateForReimbDetailsMonthlyDeclarations,
	getReimbDetailsMonthlyDeclarations
} from "redux/reimbursement/reimbursementSlice";

// Constants & Utils
import { Filters as FilterConstant } from "constants/filters";
import {
	selectedCurrentGroup,
	selectedFilterData,
	selectedOutletCodeSelector,
	selectedPageSizeSelector,
	selectedRecordCount,
	updatedSelectedPageSize
} from "redux/userList/userSlice";
import { getRowsPerPageForMonthlyDeclaration } from "utils/Generic/generic";

// Constants
import { DateTimeFormats, MONTHLY_DECLARATIONS } from "constants/global";

export type ColumnHeaderRowType = {
	title: React.ReactNode;
	dataIndex: string;
	align?: "left" | "right" | "center";
};

export type TableDataType = {
	[key: string]: string;
};

export type ExpandedRowTableProps = {
	record?: ReimbSummaryDataType;
};

export type GetReimbDetailsPayload = {
	page_number?: number;
	page_records?: number;
	from_year_month: string;
	to_year_month: string;
	outlet_code?: string;
	voucher_group_code?: string;
};

type ReimbursementDataType = {
	key: number;
	dateTime: string;
	purchaseValue: string;
	receiptNum: string;
	eVoucherNum: string;
	eVoucherValue: string;
	eVoucherQty: number;
	eVoucherType: string;
	purchaseValueRaw: number;
	eVoucherValueRaw: number;
};

type PageSizeObjectType = {
	[key: string]: number;
};

export function useExpandedRowTableForReimDeclaration({ record }: ExpandedRowTableProps) {
	const dispatch = useAppDispatch();
	const reimbursementDataList = useAppSelector(reimbDetailsSelectorMonthlyDeclarations);
	const selectedGroup = useAppSelector(selectedCurrentGroup);
	const recordCount = useAppSelector(selectedRecordCount);
	const loading = useAppSelector(getLoadingStateForReimbDetailsMonthlyDeclarations);
	const outletCode = useAppSelector(selectedOutletCodeSelector);
	const selectedFilter = useAppSelector(selectedFilterData);
	const selectedPageSize = useAppSelector(selectedPageSizeSelector);
	const [currentPage, setCurrentPage] = useState<number>(1);

	// This will provide the row per page for each group
	const pageSizeArray = useMemo(() => {
		const pageSizeObj: PageSizeObjectType = {};
		pageSizeObj[selectedGroup] = getRowsPerPageForMonthlyDeclaration(recordCount, selectedGroup);
		dispatch(updatedSelectedPageSize(pageSizeObj));
	}, [recordCount, selectedGroup, record]);

	const pageSize = useMemo(() => {
		if (selectedPageSize && record) {
			const groupPageSize = selectedPageSize[parseInt(`${record?.voucher_group_code}${record?.yearmonth}`)];
			return groupPageSize || MONTHLY_DECLARATIONS.DEFAULT_ROW_PER_PAGE;
		}
		return MONTHLY_DECLARATIONS.DEFAULT_ROW_PER_PAGE;
	}, [pageSizeArray, selectedPageSize, record]);

	const totalRecordCount = useMemo(() => {
		if (reimbursementDataList && record) {
			return reimbursementDataList[parseInt(`${record.voucher_group_code}${record.yearmonth}`)]?.record_count ?? 0;
		}
	}, [reimbursementDataList]);

	// On pagination getReimbSummary will be called
	const handlePaginationWithRecord = (pageNumber: number, recordItem?: ReimbSummaryDataType) => {
		setCurrentPage(pageNumber);
		const payload = {
			page_number: pageNumber,
			page_records: pageSize,
			from_year_month: recordItem?.yearmonth ?? "",
			to_year_month: recordItem?.yearmonth ?? "",
			outlet_code: recordItem?.outlet_code,
			voucher_group_code: recordItem?.voucher_group_code
		};
		dispatch(getReimbDetailsMonthlyDeclarations(payload));
	};

	const apiPayload: GetReimbDetailsPayload = useMemo(() => {
		return {
			from_year_month: record?.yearmonth ?? "",
			to_year_month: record?.yearmonth ?? "",
			page_number: 1,
			page_records: pageSize || MONTHLY_DECLARATIONS.DEFAULT_ROW_PER_PAGE,
			outlet_code: record?.outlet_code,
			voucher_group_code: record?.voucher_group_code
		};
	}, [record?.outlet_code, pageSize]);

	useEffect(() => {
		dispatch(getReimbDetailsMonthlyDeclarations(apiPayload));
	}, [apiPayload]);

	useEffect(() => {
		setCurrentPage(1);
	}, [outletCode, pageSize]);

	const getSortedList = (data: Array<ReimbursementDataType>, selectedFilterValue: string) => {
		if (selectedFilterValue === FilterConstant.REIMB_LIST[0].values[0].value || selectedFilterValue === "") {
			return data.sort((obj1, obj2) => new Date(obj2.dateTime).valueOf() - new Date(obj1.dateTime).valueOf());
		}
		if (selectedFilterValue === FilterConstant.REIMB_LIST[0].values[1].value) {
			return data.sort((obj1, obj2) => new Date(obj1.dateTime).valueOf() - new Date(obj2.dateTime).valueOf());
		}
		if (selectedFilterValue === FilterConstant.REIMB_LIST[0].values[2].value) {
			return orderBy(data, ["purchaseValueRaw"], ["asc"]);
		}
		if (selectedFilterValue === FilterConstant.REIMB_LIST[0].values[3].value) {
			return orderBy(data, ["purchaseValueRaw"], ["desc"]);
		}
		if (selectedFilterValue === FilterConstant.REIMB_LIST[0].values[4].value) {
			return orderBy(data, ["eVoucherValueRaw"], ["asc"]);
		}
		if (selectedFilterValue === FilterConstant.REIMB_LIST[0].values[5].value) {
			return orderBy(data, ["eVoucherValueRaw"], ["desc"]);
		}
		if (selectedFilterValue === FilterConstant.REIMB_LIST[0].values[6].value) {
			return data.sort((obj1, obj2) => obj2.eVoucherQty - obj1.eVoucherQty);
		}
		if (selectedFilterValue === FilterConstant.REIMB_LIST[0].values[7].value) {
			return orderBy(data, ["eVoucherType"], ["desc"]);
		}
	};

	// Get Updated List
	const updatedList = useMemo(() => {
		if (reimbursementDataList && record) {
			const detailsList = reimbursementDataList[parseInt(`${record.voucher_group_code}${record.yearmonth}`)]?.reimb_dtls ?? [];
			const reimData: Array<ReimbursementDataType> = [];
			detailsList?.map((item: ReimbDetailsItemMonthlyDeclarations) => {
				const details = {
					key: item.reimb_dtl_id,
					dateTime: item?.redemption_date ? moment(item?.redemption_date)?.format(DateTimeFormats.USE_DD_MMM) : "--",
					purchaseValue: `S$${item.purchase_amount}`,
					receiptNum: item.receipt_number,
					eVoucherNum: item.voucher_number,
					eVoucherValue: `S$${item.voucher_value}`,
					eVoucherQty: item.quantity,
					eVoucherType: item.voucher_type,
					eVoucherValueRaw: parseFloat(item.voucher_value),
					purchaseValueRaw: item.purchase_amount
				};
				reimData.push(details);
			});
			const selectedFilterVal: string = selectedFilter[`${record.voucher_group_code}${record.yearmonth}`] ?? "";
			return getSortedList(reimData, selectedFilterVal);
		}
	}, [reimbursementDataList, outletCode, selectedFilter]);

	return { totalRecordCount, loading, updatedList, handlePaginationWithRecord, pageSize, currentPage };
}
